import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import '@/styles/Board/TiptapToolbar.less';
import { useEditorState } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Tooltip from '@/components/Common/Tooltip';
import { FLOATING_TOOLBAR_CONSTANTS } from './constants';
import { useClickOutside } from './hooks/useClickOutside';
/**
 * Всплывающий тулбар для форматирования текста
 * Отображается при выделении текста в редакторе
 * Содержит кнопки для заголовков, списков, форматирования текста и ссылок
 *
 * @param props - Пропсы компонента
 */
const TiptapFloatingToolbar = ({ editor, zoom = 1, stagePosition = { x: 0, y: 0 }, enabledButtons, }) => {
    const [showHeadingMenu, setShowHeadingMenu] = useState(false);
    const [position, setPosition] = useState(null);
    const toolbarRef = useClickOutside(() => setShowHeadingMenu(false), true);
    /**
     * Проверяет, должна ли кнопка быть отображена
     */
    const isButtonEnabled = useCallback((buttonName) => {
        // Если список не передан, показываем все кнопки
        if (!enabledButtons) {
            return true;
        }
        return enabledButtons.includes(buttonName);
    }, [enabledButtons]);
    // Отслеживаем активные состояния форматирования
    const editorState = useEditorState({
        editor,
        selector: ({ editor: editorInstance }) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
            return ({
                isHeading1: (_a = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('heading', { level: 1 })) !== null && _a !== void 0 ? _a : false,
                isHeading2: (_b = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('heading', { level: 2 })) !== null && _b !== void 0 ? _b : false,
                isHeading3: (_c = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('heading', { level: 3 })) !== null && _c !== void 0 ? _c : false,
                isParagraph: (_d = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('paragraph')) !== null && _d !== void 0 ? _d : false,
                isBold: (_e = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('bold')) !== null && _e !== void 0 ? _e : false,
                isItalic: (_f = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('italic')) !== null && _f !== void 0 ? _f : false,
                isStrike: (_g = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('strike')) !== null && _g !== void 0 ? _g : false,
                isCode: (_h = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('code')) !== null && _h !== void 0 ? _h : false,
                isUnderline: (_j = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('underline')) !== null && _j !== void 0 ? _j : false,
                isLink: (_k = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('link')) !== null && _k !== void 0 ? _k : false,
                isBulletList: (_l = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('bulletList')) !== null && _l !== void 0 ? _l : false,
                isOrderedList: (_m = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('orderedList')) !== null && _m !== void 0 ? _m : false,
                isCodeBlock: (_o = editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.isActive('codeBlock')) !== null && _o !== void 0 ? _o : false,
            });
        },
    });
    /**
     * Обработчик установки/удаления ссылки
     */
    const setLink = useCallback(() => {
        if (!editor)
            return;
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);
        if (url === null) {
            return;
        }
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);
    /**
     * Вычисляет позицию тулбара на основе выделения текста
     *
     * @returns Позиция тулбара или null, если выделения нет
     */
    const calculatePosition = useCallback(() => {
        if (!editor)
            return null;
        const { state } = editor;
        const { from, to } = state.selection;
        // Проверяем, есть ли выделение
        if (from === to) {
            return null;
        }
        // Получаем координаты выделения
        const domSelection = window.getSelection();
        if (!domSelection || domSelection.rangeCount === 0) {
            return null;
        }
        const range = domSelection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        // Вычисляем позицию тулбара
        const { TOOLBAR_HEIGHT, OFFSET, MIN_TOP, TOOLBAR_WIDTH, MIN_LEFT_OFFSET } = FLOATING_TOOLBAR_CONSTANTS;
        let top = rect.top - TOOLBAR_HEIGHT - OFFSET;
        let left = rect.left + rect.width / 2;
        // Проверяем, не выходит ли тулбар за пределы экрана сверху
        if (top < MIN_TOP) {
            // Если выходит, показываем под выделением
            top = rect.bottom + OFFSET;
        }
        // Проверяем, не выходит ли тулбар за пределы экрана по горизонтали
        const minLeft = TOOLBAR_WIDTH / 2 + MIN_LEFT_OFFSET;
        const maxLeft = window.innerWidth - TOOLBAR_WIDTH / 2 - MIN_LEFT_OFFSET;
        if (left < minLeft) {
            left = minLeft;
        }
        else if (left > maxLeft) {
            left = maxLeft;
        }
        return { top, left };
    }, [editor]);
    // Отслеживание выделения текста и позиционирование тулбара
    useEffect(() => {
        if (!editor)
            return;
        let timeoutId = null;
        const updatePosition = () => {
            // Очищаем предыдущий таймаут
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            // Небольшая задержка перед показом тулбара
            timeoutId = setTimeout(() => {
                const newPosition = calculatePosition();
                setPosition(newPosition);
            }, FLOATING_TOOLBAR_CONSTANTS.UPDATE_DELAY);
        };
        // Обновляем позицию при изменении выделения
        editor.on('selectionUpdate', updatePosition);
        editor.on('transaction', updatePosition);
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            editor.off('selectionUpdate', updatePosition);
            editor.off('transaction', updatePosition);
        };
    }, [editor, calculatePosition]);
    // Пересчитываем позицию при изменении zoom или stagePosition, если тулбар уже отображается
    useEffect(() => {
        if (!editor || !position)
            return;
        const newPosition = calculatePosition();
        if (newPosition) {
            setPosition(newPosition);
        }
    }, [zoom, stagePosition, editor, calculatePosition, position]);
    if (!editor || !editorState || !position) {
        return null;
    }
    // Определяем, какие группы кнопок активны
    const hasHeading = isButtonEnabled('heading');
    const hasLists = isButtonEnabled('bulletList') || isButtonEnabled('orderedList') || isButtonEnabled('codeBlock');
    const hasTextFormatting = isButtonEnabled('bold') ||
        isButtonEnabled('italic') ||
        isButtonEnabled('strike') ||
        isButtonEnabled('code') ||
        isButtonEnabled('underline');
    const hasLink = isButtonEnabled('link');
    // Определяем, нужны ли разделители
    const needsSeparatorAfterHeading = hasHeading && (hasLists || hasTextFormatting || hasLink);
    const needsSeparatorAfterLists = hasLists && (hasTextFormatting || hasLink);
    const toolbarContent = (_jsxs("div", { ref: toolbarRef, className: "tiptap-toolbar floating-toolbar", style: {
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translateX(-50%)',
            zIndex: 10000,
        }, onMouseDown: (e) => {
            // Предотвращаем потерю выделения при клике на тулбар
            e.preventDefault();
        }, children: [hasHeading && (_jsxs(_Fragment, { children: [_jsx("div", { className: "toolbar-group", children: _jsxs("div", { className: "toolbar-dropdown", children: [_jsx(Tooltip, { text: "\u0417\u0430\u0433\u043E\u043B\u043E\u0432\u043E\u043A", position: "top", children: _jsxs("button", { type: "button", className: `toolbar-button ${editorState.isHeading1 || editorState.isHeading2 || editorState.isHeading3
                                            ? 'active'
                                            : ''}`, onClick: () => setShowHeadingMenu(!showHeadingMenu), "aria-label": "\u0417\u0430\u0433\u043E\u043B\u043E\u0432\u043E\u043A", children: [editorState.isHeading1
                                                ? 'H1'
                                                : editorState.isHeading2
                                                    ? 'H2'
                                                    : editorState.isHeading3
                                                        ? 'H3'
                                                        : 'P', _jsx("span", { className: "dropdown-arrow", children: "\u25BC" })] }) }), showHeadingMenu && (_jsxs("div", { className: "dropdown-menu", children: [_jsx("button", { type: "button", className: `dropdown-item ${editorState.isParagraph ? 'active' : ''}`, onClick: () => {
                                                editor.chain().focus().setParagraph().run();
                                                setShowHeadingMenu(false);
                                            }, children: "Paragraph" }), _jsx("button", { type: "button", className: `dropdown-item ${editorState.isHeading1 ? 'active' : ''}`, onClick: () => {
                                                editor.chain().focus().setHeading({ level: 1 }).run();
                                                setShowHeadingMenu(false);
                                            }, children: "Heading 1" }), _jsx("button", { type: "button", className: `dropdown-item ${editorState.isHeading2 ? 'active' : ''}`, onClick: () => {
                                                editor.chain().focus().setHeading({ level: 2 }).run();
                                                setShowHeadingMenu(false);
                                            }, children: "Heading 2" }), _jsx("button", { type: "button", className: `dropdown-item ${editorState.isHeading3 ? 'active' : ''}`, onClick: () => {
                                                editor.chain().focus().setHeading({ level: 3 }).run();
                                                setShowHeadingMenu(false);
                                            }, children: "Heading 3" })] }))] }) }), needsSeparatorAfterHeading && _jsx("div", { className: "toolbar-separator" })] })), hasLists && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "toolbar-group", children: [isButtonEnabled('bulletList') && (_jsx(Tooltip, { text: "\u041C\u0430\u0440\u043A\u0438\u0440\u043E\u0432\u0430\u043D\u043D\u044B\u0439 \u0441\u043F\u0438\u0441\u043E\u043A", position: "top", children: _jsx("button", { type: "button", className: `toolbar-button ${editorState.isBulletList ? 'active' : ''}`, onClick: () => editor.chain().focus().toggleBulletList().run(), "aria-label": "\u041C\u0430\u0440\u043A\u0438\u0440\u043E\u0432\u0430\u043D\u043D\u044B\u0439 \u0441\u043F\u0438\u0441\u043E\u043A", children: "\u2022\u2022\u2022" }) })), isButtonEnabled('orderedList') && (_jsx(Tooltip, { text: "\u041D\u0443\u043C\u0435\u0440\u043E\u0432\u0430\u043D\u043D\u044B\u0439 \u0441\u043F\u0438\u0441\u043E\u043A", position: "top", children: _jsx("button", { type: "button", className: `toolbar-button ${editorState.isOrderedList ? 'active' : ''}`, onClick: () => editor.chain().focus().toggleOrderedList().run(), "aria-label": "\u041D\u0443\u043C\u0435\u0440\u043E\u0432\u0430\u043D\u043D\u044B\u0439 \u0441\u043F\u0438\u0441\u043E\u043A", children: "=" }) })), isButtonEnabled('codeBlock') && (_jsx(Tooltip, { text: "\u0411\u043B\u043E\u043A \u043A\u043E\u0434\u0430", position: "top", children: _jsx("button", { type: "button", className: `toolbar-button ${editorState.isCodeBlock ? 'active' : ''}`, onClick: () => {
                                        if (editorState.isCodeBlock) {
                                            editor.chain().focus().toggleCodeBlock().run();
                                        }
                                        else {
                                            editor.chain().focus().setCodeBlock().run();
                                        }
                                    }, "aria-label": "\u0411\u043B\u043E\u043A \u043A\u043E\u0434\u0430", children: "[ ]" }) }))] }), needsSeparatorAfterLists && _jsx("div", { className: "toolbar-separator" })] })), (isButtonEnabled('bold') ||
                isButtonEnabled('italic') ||
                isButtonEnabled('strike') ||
                isButtonEnabled('code') ||
                isButtonEnabled('underline')) && (_jsx(_Fragment, { children: _jsxs("div", { className: "toolbar-group", children: [isButtonEnabled('bold') && (_jsx(Tooltip, { text: "\u0416\u0438\u0440\u043D\u044B\u0439", position: "top", children: _jsx("button", { type: "button", className: `toolbar-button ${editorState.isBold ? 'active' : ''}`, onClick: () => editor.chain().focus().toggleBold().run(), "aria-label": "\u0416\u0438\u0440\u043D\u044B\u0439", children: _jsx("strong", { children: "B" }) }) })), isButtonEnabled('italic') && (_jsx(Tooltip, { text: "\u041A\u0443\u0440\u0441\u0438\u0432", position: "top", children: _jsx("button", { type: "button", className: `toolbar-button ${editorState.isItalic ? 'active' : ''}`, onClick: () => editor.chain().focus().toggleItalic().run(), "aria-label": "\u041A\u0443\u0440\u0441\u0438\u0432", children: _jsx("em", { children: "I" }) }) })), isButtonEnabled('strike') && (_jsx(Tooltip, { text: "\u0417\u0430\u0447\u0435\u0440\u043A\u043D\u0443\u0442\u044B\u0439", position: "top", children: _jsx("button", { type: "button", className: `toolbar-button ${editorState.isStrike ? 'active' : ''}`, onClick: () => editor.chain().focus().toggleStrike().run(), "aria-label": "\u0417\u0430\u0447\u0435\u0440\u043A\u043D\u0443\u0442\u044B\u0439", children: _jsx("s", { children: "S" }) }) })), isButtonEnabled('code') && (_jsx(Tooltip, { text: "\u0418\u043D\u043B\u0430\u0439\u043D \u043A\u043E\u0434", position: "top", children: _jsx("button", { type: "button", className: `toolbar-button ${editorState.isCode ? 'active' : ''}`, onClick: () => {
                                    if (editorState.isCode) {
                                        editor.chain().focus().toggleCode().run();
                                    }
                                    else {
                                        editor.chain().focus().setCode().run();
                                    }
                                }, "aria-label": "\u0418\u043D\u043B\u0430\u0439\u043D \u043A\u043E\u0434", children: "</>" }) })), isButtonEnabled('underline') && (_jsx(Tooltip, { text: "\u041F\u043E\u0434\u0447\u0435\u0440\u043A\u043D\u0443\u0442\u044B\u0439", position: "top", children: _jsx("button", { type: "button", className: `toolbar-button ${editorState.isUnderline ? 'active' : ''}`, onClick: () => editor.chain().focus().toggleUnderline().run(), "aria-label": "\u041F\u043E\u0434\u0447\u0435\u0440\u043A\u043D\u0443\u0442\u044B\u0439", children: _jsx("u", { children: "U" }) }) }))] }) })), isButtonEnabled('link') && (_jsx("div", { className: "toolbar-group", children: _jsx(Tooltip, { text: "\u0421\u0441\u044B\u043B\u043A\u0430", position: "top", children: _jsx("button", { type: "button", className: `toolbar-button ${editorState.isLink ? 'active' : ''}`, onClick: setLink, "aria-label": "\u0421\u0441\u044B\u043B\u043A\u0430", children: "\uD83D\uDD17" }) }) }))] }));
    // Рендерим тулбар через портал в body для корректной работы с position: fixed
    return createPortal(toolbarContent, document.body);
};
export default TiptapFloatingToolbar;
